/**
 * OfflineProvider — owns the singleton Repo and drives background sync.
 *
 * Triggers (foreground-primary; background sync on native is best-effort and
 * lives elsewhere): on mount/login, on reconnect, on tab/app focus, a debounced
 * flush after a local edit (flushSoon), and a slow safety interval. After every
 * sync it invalidates react-query so screens re-read fresh local data.
 *
 * Offline support is for vessel + shore_staff. Admin / super_admin are
 * online-only: the sync engine doesn't run for them AND they bypass the Dexie
 * read-through cache entirely (see setOfflineEnabled / readThrough), so they
 * always read live server data and never a stale local copy.
 */
import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Capacitor } from "@capacitor/core";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { DexieRepo } from "./adapters/dexie";
import { SqliteRepo } from "./adapters/sqlite";
import { CapacitorSqlExecutor } from "./adapters/sql-executor";
import type { Repo } from "./repo";
import { runSync } from "./sync-engine";
import { rememberOfflineOwner } from "./owner";
import { onConnectivityChange, isOnline, probeReachable, setOfflineEnabled } from "./connectivity";
import { warmInspectionsForOffline, refreshInspectionAttachments } from "./warmup";

/**
 * Sync lifecycle, surfaced via the topbar pill + top progress bar:
 *   idle → syncing → success | error
 * The sync is "done" (Synced) the moment the push+pull finishes — the app is
 * fully usable then. The one-time offline WARM-UP runs AFTER that and is tracked
 * separately (`warming`), shown quietly as "Caching for offline…" so a long
 * warm-up never looks like the sync is still running. "success" auto-reverts to
 * "idle" after a moment; "error" stays until the next sync.
 */
export type SyncPhase = "idle" | "syncing" | "success" | "error";

/** Stable toast id so a manual sync shows ONE toast that goes loading→result. */
const SYNC_TOAST_ID = "icheck-sync";

/** Collapse a raw sync error into a short, friendly hint. */
function friendlySyncError(raw?: string): string {
  const m = (raw ?? "").toLowerCase();
  if (!raw || /network|fetch|timed out|timeout|offline|connection|econn|failed to fetch/.test(m)) {
    return "Check your internet connection and try again.";
  }
  return "Please try again.";
}

// Single repo for the app lifetime. Native (Capacitor iOS/Android) → durable
// SQLite in the app sandbox; web/PWA → Dexie/IndexedDB. Same Repo contract,
// guaranteed equivalent by the shared conformance suite.
const repoSingleton: Repo = Capacitor.isNativePlatform()
  ? new SqliteRepo(new CapacitorSqlExecutor())
  : new DexieRepo();

// react-query key prefixes that read offline-backed data; invalidated post-sync.
// "inspections" (plural, the list) is included so an offline-created inspection
// that just synced (temp row evicted, real row pulled) refreshes in the list.
const SYNCED_KEYS = ["inspections", "inspection", "inspection-questions", "inspection-summary", "inspection-local-status", "inspection-noms", "inspection-answer-detail", "question-findings", "rectifications", "ranks", "sms"];

interface OfflineContextValue {
  repo: Repo;
  online: boolean;
  pendingCount: number;
  /** True while a sync cycle is actively running (syncing or preparing). */
  syncing: boolean;
  /** True during the one-time post-login offline warm-up. */
  warming: boolean;
  /** Fine-grained sync lifecycle for the indicator + progress bar. */
  phase: SyncPhase;
  /** Epoch ms of the last fully-successful sync, or null. */
  lastSyncedAt: number | null;
  /** Friendly message for the last failure (cleared on success), or null. */
  lastError: string | null;
  /** Run a full sync now (manual "Sync" button / post-save flush). */
  runSyncNow: () => void;
  /** Debounced flush — call right after enqueuing a local edit. */
  flushSoon: () => void;
  /** Recompute the pending badge (call after enqueuing). */
  refreshPending: () => void;
}

const OfflineContext = React.createContext<OfflineContextValue | null>(null);

export function OfflineProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [online, setOnline] = React.useState(isOnline());
  // Mirror of `online` for the adaptive connectivity poll's closure (reads the
  // latest value without re-arming the timer on every change).
  const onlineRef = React.useRef(online);
  React.useEffect(() => { onlineRef.current = online; }, [online]);
  const [pendingCount, setPendingCount] = React.useState(0);
  // Single source of truth for sync progress/outcome (see SyncPhase). `syncing`
  // and `warming` are derived from it so existing consumers keep working.
  const [phase, setPhaseState] = React.useState<SyncPhase>("idle");
  const [lastSyncedAt, setLastSyncedAt] = React.useState<number | null>(null);
  const [lastError, setLastError] = React.useState<string | null>(null);
  // Background offline caching (the one-time warm-up) — surfaced QUIETLY and
  // separately from the sync phase, so a long warm-up doesn't look like the sync
  // is still running. The sync itself is already "done" once the pull finishes.
  const [warming, setWarming] = React.useState(false);
  const phaseRef = React.useRef<SyncPhase>("idle");
  const setPhase = React.useCallback((p: SyncPhase) => { phaseRef.current = p; setPhaseState(p); }, []);
  const syncing = phase === "syncing";

  // Offline is for vessel + shore_staff. Admin / super_admin are online-only
  // (no sync engine, no read-through cache).
  const engineEnabled = user?.role === "vessel" || user?.role === "shore_staff";
  const flushTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const warmedRef = React.useRef(false);
  // Guards two cycles driving the phase at once; success auto-reverts to idle.
  const runningRef = React.useRef(false);
  const successTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  // Always points at the latest runSyncNow, so a toast's Retry action can call it.
  const runSyncNowRef = React.useRef<() => void>(() => {});
  // Inspection ids already fully warmed (loaded from the persisted warm-up state
  // + accumulated this session). Passed as skipIds so a re-warm — at app start,
  // or a retry after a partial/failed warm — only fills the gaps instead of
  // re-downloading everything.
  const warmedIdsRef = React.useRef<Set<number>>(new Set());
  const warmStateLoadedRef = React.useRef(false);

  // Toggle the read-through cache on/off by role (kept in sync incl. logout).
  React.useEffect(() => { setOfflineEnabled(engineEnabled); }, [engineEnabled]);

  const refreshPending = React.useCallback(() => {
    void repoSingleton.pendingCount().then(setPendingCount);
  }, []);

  const doSync = React.useCallback(async (opts?: { manual?: boolean }) => {
    if (!engineEnabled) return;
    // One cycle at a time, so the indicator can't flip to "success" while
    // another cycle is still mid-flight.
    if (runningRef.current) return;
    runningRef.current = true;
    const manual = Boolean(opts?.manual);
    const wasError = phaseRef.current === "error";
    if (successTimer.current) { clearTimeout(successTimer.current); successTimer.current = null; }
    // Toast policy (SMART): a manual sync gets a single loading→result toast;
    // background syncs stay silent UNLESS your queued changes just synced, or a
    // sync FAILS (and only on the transition into failure, so it can't spam).
    const retry = { label: "Retry", onClick: () => runSyncNowRef.current() };
    if (manual) toast.loading("Syncing…", { id: SYNC_TOAST_ID });
    try {
      // Gate on REAL reachability, not navigator.onLine (which lies "online"
      // while every request fails). Without this, an offline tick runs the
      // warm-up — flooding doomed requests AND marking warm "done" so it never
      // caches when actually online.
      const reachable = await probeReachable();
      setOnline(reachable);
      if (!reachable) {
        if (manual) {
          toast.error("Can't sync — you're offline.", {
            id: SYNC_TOAST_ID,
            description: "Your changes are saved and will sync when you reconnect.",
          });
        }
        setPhase("idle");
        return;
      }
      setPhase("syncing");
      let pendingBefore = 0;
      try { pendingBefore = await repoSingleton.pendingCount(); } catch { /* best-effort */ }

      const summary = await runSync(repoSingleton);
      if (summary.pulled || summary.pushed > 0 || (summary.createdInspections ?? 0) > 0) {
        for (const k of SYNCED_KEYS) void queryClient.invalidateQueries({ queryKey: [k] });
      }
      // Attachments added offline just uploaded — refresh those inspections'
      // aggregated attachment caches (the offline Attachments page reads them)
      // so the new files show offline without an explicit online visit. Runs
      // while we're online (right after the sync); best-effort.
      if (summary.attachmentInspectionIds?.length) {
        void refreshInspectionAttachments(repoSingleton, summary.attachmentInspectionIds)
          .then(() => { for (const k of SYNCED_KEYS) void queryClient.invalidateQueries({ queryKey: [k] }); })
          .catch(() => {});
      }
      // Notify any screen still sitting on a temp (offline-created) inspection so
      // it can redirect to the now-real server id.
      if (summary.idmap && typeof window !== "undefined") {
        for (const [tempId, serverId] of Object.entries(summary.idmap)) {
          window.dispatchEvent(new CustomEvent("offline:inspection-synced", { detail: { tempId: Number(tempId), serverId } }));
        }
      }
      if (summary.error) {
        const msg = friendlySyncError(summary.error);
        setLastError(msg);
        setPhase("error");
        if (manual) toast.error("Sync failed", { id: SYNC_TOAST_ID, description: msg, action: retry });
        else if (!wasError) toast.error("Sync failed", { description: msg, action: retry });
        return;
      }
      // Push + pull succeeded → the app is fully usable NOW. Mark it "Synced"
      // immediately; the offline warm-up below runs quietly after.
      setLastSyncedAt(Date.now());
      setLastError(null);
      setPhase("success");
      if (manual) {
        toast.success("Synced", { id: SYNC_TOAST_ID });
      } else if (pendingBefore > 0) {
        toast.success(`${pendingBefore} change${pendingBefore === 1 ? "" : "s"} synced`);
      }
      // "Success" is transient — fade back to the steady idle indicator.
      successTimer.current = setTimeout(() => setPhase("idle"), 2500);

      // Then QUIETLY pre-cache every in-scope inspection + observations +
      // rectifications for offline. Surfaced as a subtle "Caching for offline…"
      // via `warming` — NOT the sync spinner / progress bar — so a long warm-up
      // never looks like the sync is stuck. Once per session, but only marked
      // "done" AFTER a real (reachable) warm completes, so a flaky tick can't
      // permanently skip it: it RETRIES on later online ticks if it didn't
      // complete (network loss mid-warm) — re-warming after reconnect, not just
      // delta-syncing — and resumes incrementally via warmedIds so retries /
      // app-starts only fill the gaps. Best-effort: never fails the sync.
      if (!warmedRef.current) {
        if (!warmStateLoadedRef.current) {
          warmStateLoadedRef.current = true;
          try {
            const prev = await repoSingleton.kvGet<{ complete: boolean; ids: number[] }>("warmup:state");
            for (const wid of prev?.ids ?? []) warmedIdsRef.current.add(wid);
          } catch { /* best-effort */ }
        }
        setWarming(true);
        try {
          const { complete, warmedIds } = await warmInspectionsForOffline(
            repoSingleton,
            { userId: user?.id ?? null, role: user?.role ?? null, rank: user?.rank ?? null, companyId: user?.companyId ?? null },
            { skipIds: warmedIdsRef.current },
          );
          for (const wid of warmedIds) warmedIdsRef.current.add(wid);
          try { await repoSingleton.kvPut("warmup:state", { complete, ids: [...warmedIdsRef.current] }); } catch { /* best-effort */ }
          if (complete) warmedRef.current = true; // stop re-warming only on a clean run
          for (const k of SYNCED_KEYS) void queryClient.invalidateQueries({ queryKey: [k] });
        } catch { /* best-effort */ } finally {
          setWarming(false);
        }
      }
    } catch (err) {
      const msg = friendlySyncError(err instanceof Error ? err.message : undefined);
      setLastError(msg);
      setPhase("error");
      if (manual) toast.error("Sync failed", { id: SYNC_TOAST_ID, description: msg, action: retry });
      else if (!wasError) toast.error("Sync failed", { description: msg, action: retry });
    } finally {
      runningRef.current = false;
      refreshPending();
    }
  }, [engineEnabled, queryClient, refreshPending, user, setPhase]);

  const runSyncNow = React.useCallback(() => { void doSync({ manual: true }); }, [doSync]);
  React.useEffect(() => { runSyncNowRef.current = runSyncNow; }, [runSyncNow]);
  const flushSoon = React.useCallback(() => {
    refreshPending();
    if (flushTimer.current) clearTimeout(flushTimer.current);
    flushTimer.current = setTimeout(() => void doSync(), 1500);
  }, [doSync, refreshPending]);

  // Confirm real reachability (HEAD/GET /api/healthz) rather than trusting
  // navigator.onLine, which can report "online" while every request fails.
  // Drives the status indicator AND kicks a sync when we come back online.
  const refreshOnline = React.useCallback(async () => {
    const reachable = await probeReachable();
    setOnline((prev) => {
      if (reachable && !prev) void doSync(); // transitioned offline → online
      return reachable;
    });
  }, [doSync]);

  // Bootstrap + triggers.
  React.useEffect(() => {
    if (!user) return;

    // Fresh login → refill + refresh the local cache:
    //  1. Re-arm the warm-up (warmedRef otherwise persists for the provider
    //     lifetime, so a second login in the same tab would skip it).
    //  2. If the cached data belongs to a DIFFERENT user, wipe the repo first
    //     (stale rows + cursor + the other user's outbox) so this user gets a
    //     clean full pull rather than deltas-since-the-other-user's-cursor.
    warmedRef.current = false;
    // Re-arm incremental warm tracking too (a different user's repo.clear() below
    // wipes the persisted warmup:state; same-user keeps it for a fast resume).
    warmStateLoadedRef.current = false;
    warmedIdsRef.current = new Set();
    void (async () => {
      try {
        const OWNER_KEY = "offline:ownerUserId";
        const cachedOwner = localStorage.getItem(OWNER_KEY);
        if (cachedOwner !== String(user.id)) {
          // The login screen already warned, and had the previous owner's
          // unsynced changes discarded explicitly (see offline/owner.ts).
          if (engineEnabled) await repoSingleton.clear(); // also clears the cursor → next pull is full
          localStorage.setItem(OWNER_KEY, String(user.id));
        }
        rememberOfflineOwner(user);
      } catch { /* localStorage / clear are best-effort */ }
      refreshPending();
      void refreshOnline();
      void doSync();
    })();

    // Re-probe on browser connectivity events (don't trust the event's value).
    const offConn = onConnectivityChange(() => void refreshOnline());
    const onFocus = () => { if (document.visibilityState === "visible") { void refreshOnline(); void doSync(); } };
    document.addEventListener("visibilitychange", onFocus);
    // Probe connectivity so the indicator stays accurate AND a reconnect kicks
    // a sync promptly. Poll FAST while offline (the browser `online` / Capacitor
    // networkStatusChange events are unreliable on iOS — a dropped `offline`
    // event even makes the later `online` emit dedupe away — so don't depend on
    // them to notice reconnection) and slowly while online (don't hammer).
    let statusTimer: ReturnType<typeof setTimeout>;
    let statusStopped = false;
    const scheduleStatus = () => {
      statusTimer = setTimeout(async () => {
        await refreshOnline();
        if (!statusStopped) scheduleStatus();
      }, onlineRef.current ? 20_000 : 3_000);
    };
    scheduleStatus();
    // Slow safety interval (cache stays warm without hammering).
    const iv = setInterval(() => void doSync(), 5 * 60 * 1000);

    return () => {
      offConn();
      document.removeEventListener("visibilitychange", onFocus);
      statusStopped = true;
      clearTimeout(statusTimer);
      clearInterval(iv);
      if (flushTimer.current) clearTimeout(flushTimer.current);
      if (successTimer.current) clearTimeout(successTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const value: OfflineContextValue = { repo: repoSingleton, online, pendingCount, syncing, warming, phase, lastSyncedAt, lastError, runSyncNow, flushSoon, refreshPending };
  return <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>;
}

export function useOffline(): OfflineContextValue {
  const ctx = React.useContext(OfflineContext);
  if (!ctx) throw new Error("useOffline must be used within <OfflineProvider>");
  return ctx;
}

/** Direct repo access for non-React call sites. */
export function getRepo(): Repo { return repoSingleton; }
